import { useEffect, useRef } from "react";
import {
  InsuranceExclusionsResponseDto,
  useGetInsuranceExclusionsQuery,
} from "pages/dashboard/home/dashboardService";

const INSURANCE_PERSONALIZED_URL_PREFIX = "insurancePersonalizedUrl";

export function getInsurancePersonalizedUrlSessionKey(nhDealApplicationId: string): string {
  return `${INSURANCE_PERSONALIZED_URL_PREFIX}_${nhDealApplicationId}`;
}

export function getCachedInsurancePersonalizedUrl(nhDealApplicationId: string): string | null {
  if (!nhDealApplicationId) {
    return null;
  }

  return sessionStorage.getItem(getInsurancePersonalizedUrlSessionKey(nhDealApplicationId));
}

export function clearCachedInsurancePersonalizedUrl(nhDealApplicationId: string): void {
  if (!nhDealApplicationId) {
    return;
  }

  sessionStorage.removeItem(getInsurancePersonalizedUrlSessionKey(nhDealApplicationId));
}

export interface InsuranceEligibilityResult {
  isEligibleForInsurance: boolean;
  insurancePersonalizedUrl?: string;
  exclusionReasons: InsuranceExclusionsResponseDto["exclusionReasons"];
  isLoading: boolean;
  isError: boolean;
}

export function useInsuranceEligibility(
  nhDealApplicationId: string,
  userId: string,
  insuranceAdEnabled?: boolean,
  uqualProgramEligible?: boolean,
): InsuranceEligibilityResult {
  const shouldSkipExclusionsQuery =
    !insuranceAdEnabled ||
    !nhDealApplicationId ||
    !userId ||
    uqualProgramEligible !== false;

  const { data, isLoading, isError } = useGetInsuranceExclusionsQuery(
    { nhDealApplicationId, userId },
    {
      skip: shouldSkipExclusionsQuery,
    },
  );

  const previousDealIdRef = useRef<string>();

  useEffect(() => {
    const previousDealId = previousDealIdRef.current;
    if (previousDealId && previousDealId !== nhDealApplicationId) {
      clearCachedInsurancePersonalizedUrl(previousDealId);
    }

    previousDealIdRef.current = nhDealApplicationId;
  }, [nhDealApplicationId]);

  useEffect(() => {
    if (!nhDealApplicationId || uqualProgramEligible === false) {
      return;
    }

    clearCachedInsurancePersonalizedUrl(nhDealApplicationId);
  }, [nhDealApplicationId, uqualProgramEligible]);

  useEffect(() => {
    if (!nhDealApplicationId) {
      return;
    }

    const sessionKey = getInsurancePersonalizedUrlSessionKey(nhDealApplicationId);
    if (data?.isEligibleForInsurance && data.insurancePersonalizedUrl) {
      sessionStorage.setItem(sessionKey, data.insurancePersonalizedUrl);
    } else {
      sessionStorage.removeItem(sessionKey);
    }
  }, [data, nhDealApplicationId]);

  return {
    isEligibleForInsurance: data?.isEligibleForInsurance ?? false,
    insurancePersonalizedUrl: data?.isEligibleForInsurance ? data.insurancePersonalizedUrl : undefined,
    exclusionReasons: data?.exclusionReasons ?? [],
    isLoading,
    isError,
  };
}
